import { t } from "@/lib/i18n/interpolate";
import {
  type Format,
  targetLine,
  wordTargets,
  wordTotal,
} from "@/lib/tool-kit/business-templates/budget";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  countWords,
  parseWholeCount,
  splitItems,
  splitLines,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * An executive summary assembled from the writer's own answers: the situation and
 * purpose, the problem, the findings, the recommendation, and what happens next.
 *
 * The length target is a share of the full document — 5–10% is the range university
 * and workplace guides usually give — capped at one page by default, since a summary
 * longer than a page has stopped being one. Both are conventions, so both can be
 * changed, and an instructor's limit always wins.
 */

export const DOC_KINDS = ["report", "plan", "proposal"] as const;
export type DocKind = (typeof DOC_KINDS)[number];

export const PARTS = [
  "purpose",
  "problem",
  "findings",
  "recommendation",
  "next",
] as const;
export type Part = (typeof PARTS)[number];

/** Percent of the summary's words per part. */
export const PART_SHARES: Record<Part, number> = {
  purpose: 10,
  problem: 20,
  findings: 35,
  recommendation: 25,
  next: 10,
};

export const SHARE_CHOICES = ["5", "8", "10"] as const;
export type LengthUnit = "words" | "pages";

/** Words on a single-spaced page — the figure used to turn pages into words. */
export const WORDS_PER_PAGE = 500;
/** The cap, when switched on: one page. */
export const PAGE_CAP = WORDS_PER_PAGE;

export interface ExecutiveSummaryInput {
  title: string;
  kind: DocKind;
  purpose: string;
  problem: string;
  /** One finding per line. */
  findings: string;
  recommendation: string;
  next: string;
  length: string;
  unit: LengthUnit;
  share: (typeof SHARE_CHOICES)[number];
  cap: boolean;
}

export interface SummaryTarget {
  /** The share of the document, before any cap. */
  raw: number;
  /** The target in words, rounded to the nearest 10 (5 below 100). */
  words: number;
  capped: boolean;
  /** The full document's length in words. */
  documentWords: number;
}

/** The target length of the summary, or null when the document length is unusable. */
export function summaryTarget(
  input: Pick<ExecutiveSummaryInput, "length" | "unit" | "share" | "cap">
): SummaryTarget | null {
  const length =
    input.unit === "pages"
      ? parseWholeCount(input.length)
      : wordTotal(input.length, 1, 10_000_000);
  if (length === null || length <= 0) {
    return null;
  }
  const documentWords =
    input.unit === "pages" ? length * WORDS_PER_PAGE : length;
  const raw = (documentWords * Number(input.share)) / 100;
  const capped = input.cap && raw > PAGE_CAP;
  const chosen = capped ? PAGE_CAP : raw;
  const step = chosen < 100 ? 5 : 10;
  return {
    raw,
    words: Math.max(step, Math.round(chosen / step) * step),
    capped,
    documentWords,
  };
}

/** Words the writer has typed into the five answers. */
export function draftWords(input: ExecutiveSummaryInput): number {
  return PARTS.reduce((sum, part) => sum + countWords(input[part]), 0);
}

export type LengthStatus = "empty" | "under" | "on" | "over";

/** Within 10% of the target counts as on target. */
export const ON_TARGET_TOLERANCE = 0.1;

export function lengthStatus(
  draft: number,
  target: number | null
): LengthStatus {
  if (draft === 0) {
    return "empty";
  }
  if (target === null) {
    return "on";
  }
  if (draft > target * (1 + ON_TARGET_TOLERANCE)) {
    return "over";
  }
  if (draft < target * (1 - ON_TARGET_TOLERANCE)) {
    return "under";
  }
  return "on";
}

export interface ExecutiveSummaryLabels {
  defaultTitle: string;
  titleWithName: string;
  subtitle: Record<DocKind, string>;
  subtitleTarget: string;
  parts: Record<DocKind, Record<Part, string>>;
  tips: Record<DocKind, Record<Part, string>>;
  prompts: Record<DocKind, Record<Part, string>>;
  target: string;
  targetShare: string;
  checkHeading: string;
  checks: readonly string[];
}

function partBlocks(
  part: Part,
  input: ExecutiveSummaryInput,
  labels: ExecutiveSummaryLabels,
  words: number | null,
  fmt: Format
): DocBlock[] {
  const intro: DocBlock = {
    kind: "guidance",
    text: `${targetLine(PART_SHARES[part], words, { withWords: labels.target, shareOnly: labels.targetShare }, fmt)} ${labels.tips[input.kind][part]}`,
  };
  if (part === "findings") {
    const items = splitItems(input.findings);
    return items.length > 0
      ? [
          intro,
          {
            kind: "list",
            list: { style: "bullet", items: items.map((text) => ({ text })) },
          },
        ]
      : [intro, { kind: "prompt", text: labels.prompts[input.kind][part] }];
  }
  const paragraphs = splitLines(input[part]);
  return [
    intro,
    ...(paragraphs.length > 0
      ? paragraphs.map((text): DocBlock => ({ kind: "paragraph", text }))
      : [
          {
            kind: "prompt",
            text: labels.prompts[input.kind][part],
          } as DocBlock,
        ]),
  ];
}

export function buildExecutiveSummary(
  input: ExecutiveSummaryInput,
  labels: ExecutiveSummaryLabels,
  fmt: Format
): DocumentModel {
  const target = summaryTarget(input);
  const targets = wordTargets(PARTS, PART_SHARES, target?.words ?? null, 5);
  const title = input.title.trim();
  const sections: DocSection[] = PARTS.map((part) => ({
    heading: labels.parts[input.kind][part],
    blocks: partBlocks(part, input, labels, targets?.[part] ?? null, fmt),
  }));
  return {
    title: title
      ? t(labels.titleWithName, { name: title })
      : labels.defaultTitle,
    subtitle: target
      ? `${labels.subtitle[input.kind]} · ${t(labels.subtitleTarget, { words: fmt(target.words) })}`
      : labels.subtitle[input.kind],
    sections: [
      ...sections,
      {
        heading: labels.checkHeading,
        blocks: [
          {
            kind: "checklist",
            items: labels.checks.map((text) => ({ text })),
          },
        ],
      },
    ],
  };
}
